import React from 'react';

import {Button} from '@kit';

import {useCharacter} from '../../character-context';
import type {CharacterAction} from '../../model/collect-effects';
import {formatDice, parseDice} from '../../model/dice';
import {abilityLabel, saveLabel} from '../../model/labels';
import {healedHitPoints} from '../../model/procedures';
import {DamageTypeIcon} from '../damage-type-icon';
import {RollButton, useRoll} from '../dice-roller';
import {ProcedureButton} from './procedure-button';
import {UseButton} from './use-button';
import {useFeatureAction} from './use-feature-action';

import css from '../sheet.module.css';

type Props = {
	action: CharacterAction;
	/** колонка длительности перед дистанцией: для вкладки заклинаний */
	withCastingTime?: boolean;
};

/**
 * Ячейки строки действия особенности. Процедура открывает своё окно; урон и лечение применяются броском,
 * который тратит использование; остальное — кнопкой, которая просто тратит использование.
 */
export const FeatureActionCells: React.FC<Props> = ({action, withCastingTime}) => {
	const {save, damage, healing, area, procedure} = action;

	return (
		<>
			{withCastingTime && <td />}
			<td>{area}</td>
			<td>
				<span className={css.cellButtons}>
					{procedure ? (
						<ProcedureButton action={action} procedure={procedure} />
					) : damage || healing ? (
						<SpentMark action={action} />
					) : (
						<UseButton action={action} />
					)}
					{save && (
						<span title={`Испытание: ${abilityLabel(save.ability)}, СЛ ${save.dc}`}>
							{saveLabel([save.ability], save.dc)}
						</span>
					)}
				</span>
			</td>
			<td>
				{damage && <DamageButton action={action} damage={damage} />}
				{healing && <HealingButton action={action} healing={healing} />}
			</td>
			<td className={css.notesCell} />
		</>
	);
};

/** у действия с броском отдельной кнопки нет: видно только, что использования кончились */
const SpentMark: React.FC<{action: CharacterAction}> = ({action}) => {
	const {hasUses, canUse} = useFeatureAction(action);
	if (!hasUses || canUse) return null;
	return (
		<Button size="small" disabled>
			Применено
		</Button>
	);
};

const DamageButton: React.FC<{action: CharacterAction; damage: string}> = ({action, damage}) => {
	const {canUse, hasUses, spend} = useFeatureAction(action);
	const formula = parseDice(damage);
	return (
		<RollButton
			title={`${action.title}: урон`}
			formula={formula}
			appearance="secondary"
			size="small"
			onRoll={() => hasUses && canUse && spend()}
		>
			<span className={css.damageLabel}>
				{formatDice(formula)}
				{action.damageType && <DamageTypeIcon type={action.damageType} />}
			</span>
		</RollButton>
	);
};

/** бросок лечения себя: Второе дыхание — тратит использование и прибавляет ПЗ */
const HealingButton: React.FC<{action: CharacterAction; healing: string}> = ({action, healing}) => {
	const {update} = useCharacter();
	const {canUse, hasUses, spend} = useFeatureAction(action);
	const roll = useRoll();
	const formula = parseDice(healing);

	const heal = () => {
		if (hasUses) spend();
		roll({
			title: `${action.title}: лечение`,
			formula,
			onTotal: (total) => update((current) => ({hitPoints: healedHitPoints(current.hitPoints, total)})),
		});
	};

	return (
		<Button size="small" disabled={hasUses && !canUse} onClick={heal}>
			{formatDice(formula)} лечение
		</Button>
	);
};
